const cache = new Map()
const pendingRequests = new Map()

export async function getRemoteImageSize(url, options = {}) {
  if (cache.has(url)) {
    return cache.get(url)
  }

  if (pendingRequests.has(url)) {
    return pendingRequests.get(url)
  }

  const initialBytes = options.initialBytes || 64 * 1024
  const maxBytes = options.maxBytes || 512 * 1024
  const signal = options.signal

  const promise = tryRead(url, initialBytes, maxBytes, signal)
    .then((result) => {
      cache.set(url, result)
      pendingRequests.delete(url)
      return result
    })
    .catch((error) => {
      pendingRequests.delete(url)
      throw error
    })

  pendingRequests.set(url, promise)
  return promise
}

async function tryRead(url, bytes, maxBytes, signal) {
  const buf = await fetchRange(url, 0, Math.max(0, bytes - 1), signal)
  const view = new DataView(buf)

  const png = parsePNG(view)
  if (png) return png

  const jpg = parseJPEG(view)
  if (jpg) return jpg

  const webp = parseWEBP(view)
  if (webp) return webp

  if (bytes < maxBytes) {
    const next = Math.min(maxBytes, bytes * 4)
    return tryRead(url, next, maxBytes, signal)
  }

  throw new Error('Could not determine image size from header bytes.')
}

async function fetchRange(url, start, end, signal) {
  const res = await fetch(url, {
    headers: { Range: `bytes=${start}-${end}` },
    signal,
  })

  if (!res.ok && res.status !== 206) {
    throw new Error(`Failed to fetch (${res.status} ${res.statusText})`)
  }
  return res.arrayBuffer()
}

/* ---------- PNG ---------- */
function parsePNG(view) {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  for (let i = 0; i < sig.length; i++) {
    if (view.getUint8(i) !== sig[i]) return null
  }
  const width = view.getUint32(16, false)
  const height = view.getUint32(20, false)
  return { width, height, format: 'png' }
}

/* ---------- JPEG ---------- */
function parseJPEG(view) {
  const len = view.byteLength
  if (len < 4 || view.getUint16(0, false) !== 0xffd8) return null

  let offset = 2
  while (offset + 3 < len) {
    if (view.getUint8(offset) !== 0xff) {
      offset++
      continue
    }
    const marker = view.getUint8(offset + 1)
    const size = view.getUint16(offset + 2, false)

    const isSOF =
      (marker >= 0xc0 && marker <= 0xc3) ||
      (marker >= 0xc5 && marker <= 0xc7) ||
      (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf)

    if (isSOF) {
      const seg = offset + 4
      if (seg + 5 < len) {
        const height = view.getUint16(seg + 1, false)
        const width = view.getUint16(seg + 3, false)
        return { width, height, format: 'jpeg' }
      }
      return null
    }

    if (size < 2) break
    offset += 2 + size
  }
  return null
}

/* ---------- WEBP ---------- */
function parseWEBP(view) {
  const readStr = (start, n) =>
    String.fromCharCode(
      ...Array.from({ length: n }, (_, i) => view.getUint8(start + i)),
    )

  if (view.byteLength < 16) return null
  if (readStr(0, 4) !== 'RIFF' || readStr(8, 4) !== 'WEBP') return null

  const fourcc = readStr(12, 4)
  if (fourcc === 'VP8X' && view.byteLength >= 30) {
    const w =
      1 +
      (view.getUint8(24) | (view.getUint8(25) << 8) | (view.getUint8(26) << 16))
    const h =
      1 +
      (view.getUint8(27) | (view.getUint8(28) << 8) | (view.getUint8(29) << 16))
    return { width: w, height: h, format: 'webp' }
  }
  return null
}
